import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminShell from './AdminShell';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';
import {
    getAdminMe,
  listCommissionRequests,
  approveCommissionRequest,
  rejectCommissionRequest,
  markCommissionPaid,
  getCommissionSummary,
  getCommissionEarnings
} from '../api';

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid'
};

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'paid', label: 'Paid' }
];

const CommissionManagement = () => {
      const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
    useEffect(() => {
    getAdminMe()
      .then(({ data }) => {
        setAdmin(data.admin);
      })
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        navigate('/login');
      });
  }, [navigate]);
  const [requests, setRequests] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [actionLoading, setActionLoading] = useState(null);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [notification, setNotification] = useState(null);

const [actionDialog, setActionDialog] = useState({
  open: false,
  type: null,
  request: null,
  remarks: ''
});

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  /* ---------------------------------
     SUMMARY
  --------------------------------- */

  const [summary, setSummary] = useState({
    totalEarned: 0,
    availableAmount: 0,
    pendingAmount: 0,
    approvedAmount: 0,
    rejectedAmount: 0,
    paidAmount: 0,
    commissionCount: 0,
    withdrawalCount: 0
  });

  const [summaryLoading, setSummaryLoading] = useState(true);

  const loadSummary = async () => {
    try {
      setSummaryLoading(true);

      const response = await getCommissionSummary();

      setSummary(
        response.data || {
          totalEarned: 0,
          availableAmount: 0,
          pendingAmount: 0,
          approvedAmount: 0,
          rejectedAmount: 0,
          paidAmount: 0,
          commissionCount: 0,
          withdrawalCount: 0
        }
      );
    } catch (err) {
      console.error(
        'Failed to load commission summary:',
        err
      );

      setError(
        err.response?.data?.message ||
        'Failed to load commission summary.'
      );
    } finally {
      setSummaryLoading(false);
    }
  };

  /* ---------------------------------
     EARNINGS
  --------------------------------- */

  const [earnings, setEarnings] = useState([]);

  const [earningsLoading, setEarningsLoading] =
    useState(true);

  const [earningsPagination, setEarningsPagination] =
    useState({
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    });

  const loadEarnings = async (page = 1) => {
    try {
      setEarningsLoading(true);

      const response = await getCommissionEarnings({
        page
      });

      const data = response.data;

      setEarnings(data.earnings || []);

      setEarningsPagination(
        data.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0
        }
      );
    } catch (err) {
      console.error(
        'Failed to load commission earnings:',
        err
      );

      setError(
        err.response?.data?.message ||
        'Failed to load commission earnings.'
      );
    } finally {
      setEarningsLoading(false);
    }
  };

  /* ---------------------------------
     LOAD REQUESTS
  --------------------------------- */

  const loadRequests = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const response = await listCommissionRequests({
        status: statusFilter,
        search: searchTerm,
        page
      });

      const data = response.data;

      setRequests(data.requests || []);

      setPagination(
        data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0
        }
      );
    } catch (err) {
      console.error(
        'Failed to load commission requests:',
        err
      );

      setError(
        err.response?.data?.message ||
        'Failed to load commission requests.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests(1);
  }, [statusFilter]);

  useEffect(() => {
    loadSummary();
    loadEarnings(1);
  }, []);

  /* ---------------------------------
     REFRESH COMMISSION DATA
  --------------------------------- */

  const refreshCommissionData = async () => {
    await Promise.all([
      loadSummary(),
      loadEarnings(earningsPagination.page)
    ]);
  };

  /* ---------------------------------
     SEARCH
  --------------------------------- */

  const handleSearch = (e) => {
    e.preventDefault();
    loadRequests(1);
  };
  const showNotification = (type, message) => {
  setNotification({
    type,
    message
  });

  setTimeout(() => {
    setNotification(null);
  }, 4000);
};

  /* ---------------------------------
     APPROVE
  --------------------------------- */

  const handleApprove = async (request) => {
  try {
    setActionLoading(request.id);
    setError('');

    await approveCommissionRequest(request.id);

    setSelectedRequest(null);

    await loadRequests(pagination.page);
    await refreshCommissionData();

    showNotification(
      'success',
      `Commission withdrawal of ${formatAmount(request.amount)} approved successfully.`
    );
  } catch (err) {
    console.error(
      'Failed to approve commission request:',
      err
    );

    showNotification(
      'error',
      err.response?.data?.message ||
      'Failed to approve commission request.'
    );
  } finally {
    setActionLoading(null);

    setActionDialog({
      open: false,
      type: null,
      request: null,
      remarks: ''
    });
  }
};

  /* ---------------------------------
     REJECT
  --------------------------------- */

  const handleReject = async (request) => {
  const remarks = actionDialog.remarks;

  if (!remarks || !remarks.trim()) {
    showNotification(
      'error',
      'Please enter a rejection reason.'
    );
    return;
  }

  try {
    setActionLoading(request.id);
    setError('');

    await rejectCommissionRequest(
      request.id,
      remarks.trim()
    );

    setSelectedRequest(null);

    await loadRequests(pagination.page);
    await refreshCommissionData();

    showNotification(
      'success',
      `Commission withdrawal of ${formatAmount(request.amount)} rejected.`
    );
  } catch (err) {
    console.error(
      'Failed to reject commission request:',
      err
    );

    showNotification(
      'error',
      err.response?.data?.message ||
      'Failed to reject commission request.'
    );
  } finally {
    setActionLoading(null);

    setActionDialog({
      open: false,
      type: null,
      request: null,
      remarks: ''
    });
  }
};

  /* ---------------------------------
     MARK PAID
  --------------------------------- */

  const handleMarkPaid = async (request) => {
  try {
    setActionLoading(request.id);
    setError('');

    await markCommissionPaid(request.id);

    setSelectedRequest(null);

    await loadRequests(pagination.page);
    await refreshCommissionData();

    showNotification(
      'success',
      `Commission payment of ${formatAmount(request.amount)} marked as paid.`
    );
  } catch (err) {
    console.error(
      'Failed to mark commission as paid:',
      err
    );

    showNotification(
      'error',
      err.response?.data?.message ||
      'Failed to mark commission as paid.'
    );
  } finally {
    setActionLoading(null);

    setActionDialog({
      open: false,
      type: null,
      request: null,
      remarks: ''
    });
  }
};
  /* ---------------------------------
     FORMATTERS
  --------------------------------- */

  const formatAmount = (amount) => {
    return `₹${Number(amount || 0).toLocaleString(
      'en-IN',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    )}`;
  };

  const formatDate = (date) => {
    if (!date) {
      return '—';
    }

    return new Date(date).toLocaleDateString(
      'en-IN',
      {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }
    );
  };

  const getInitials = (name) => {
    if (!name) {
      return 'SE';
    }

    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  };

  /* ---------------------------------
     RENDER
  --------------------------------- */

  return (
    <AdminShell
    admin={admin}
    badges={{}}
    pageTitle="Commission Management"
  >
    <div className="glass-page">


      {/* IN-PAGE NOTIFICATION */}

      {notification && (
        <div
          className={`commission-notification commission-notification-${notification.type}`}
        >
          <div>
            <strong>
              {notification.type === 'success'
                ? 'Success'
                : 'Error'}
            </strong>

            <div>
              {notification.message}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* PAGE HEADER */}

      <div className="glass-page-header">
        <div>
          <h1>Commission Management</h1>

          <p>
            Review sales executive withdrawal requests
            and commission payments.
          </p>
        </div>
      </div>


      {/* SUMMARY */}

      <div className="glass-kpi-grid">

        <div className="glass-panel glass-kpi-card">
          <span className="glass-kpi-label">
            Total Earned
          </span>

          <strong className="glass-kpi-value">
            {summaryLoading
              ? '—'
              : formatAmount(
                  summary.totalEarned
                )}
          </strong>

          <span className="glass-kpi-subtext">
            {summary.commissionCount} commission entries
          </span>
        </div>


        <div className="glass-panel glass-kpi-card">
          <span className="glass-kpi-label">
            Available Commission
          </span>

          <strong className="glass-kpi-value">
            {summaryLoading
              ? '—'
              : formatAmount(
                  summary.availableAmount
                )}
          </strong>

          <span className="glass-kpi-subtext">
            Available for withdrawal
          </span>
        </div>


        <div className="glass-panel glass-kpi-card">
          <span className="glass-kpi-label">
            Pending Withdrawals
          </span>

          <strong className="glass-kpi-value">
            {summaryLoading
              ? '—'
              : formatAmount(
                  summary.pendingAmount
                )}
          </strong>

          <span className="glass-kpi-subtext">
            Awaiting review
          </span>
        </div>


        <div className="glass-panel glass-kpi-card">
          <span className="glass-kpi-label">
            Paid Commission
          </span>

          <strong className="glass-kpi-value">
            {summaryLoading
              ? '—'
              : formatAmount(
                  summary.paidAmount
                )}
          </strong>

          <span className="glass-kpi-subtext">
            Completed withdrawals
          </span>
        </div>

      </div>


      {/* WITHDRAWAL REQUESTS */}

      <div className="glass-panel glass-table-panel">

        {/* SECTION HEADER */}

        <div
          style={{
            marginBottom: 18
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18
            }}
          >
            Withdrawal Requests
          </h2>

          <p
            style={{
              margin: '6px 0 0',
              opacity: 0.65,
              fontSize: 13
            }}
          >
            Review and process sales executive commission withdrawals.
          </p>
        </div>


        {/* TOOLBAR */}

        <div className="glass-toolbar">

          <div className="glass-tabs">

            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={
                  statusFilter === filter.key
                    ? 'glass-tab active'
                    : 'glass-tab'
                }
                onClick={() =>
                  setStatusFilter(filter.key)
                }
              >
                {filter.label}
              </button>
            ))}

          </div>


          <form
            className="glass-search"
            onSubmit={handleSearch}
          >
            <input
              type="text"
              placeholder="Search executive..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
            />

            <button type="submit">
              Search
            </button>
          </form>

        </div>


        {/* ERROR */}

        {error && (
          <div
            className="glass-empty-state"
            style={{
              color: '#b42318',
              marginBottom: 16
            }}
          >
            {error}
          </div>
        )}


        {/* REQUEST LOADING / TABLE */}

        {loading ? (
          <div className="glass-empty-state">
            Loading commission requests...
          </div>
        ) : requests.length === 0 ? (

          <div className="glass-empty-state">
            No commission requests found.
          </div>

        ) : (

          <div className="dash-directory-table-wrap">

            <table className="dash-directory-table">

              <thead>

                <tr>

                  <th>
                    Sales Executive
                  </th>

                  <th>
                    Executive Code
                  </th>

                  <th>
                    Amount
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Requested On
                  </th>

                  <th>
                    Reviewed On
                  </th>

                  <th>
                    Paid On
                  </th>

                  <th>
                    Actions
                  </th>

                </tr>

              </thead>


              <tbody>

                {requests.map((request) => (

                  <tr key={request.id}>

                    {/* EXECUTIVE */}

                    <td>

                      <div className="glass-vendor-cell">

                        <div className="glass-avatar-tile">
                          {getInitials(
                            request.executive_name
                          )}
                        </div>

                        <div>

                          <strong>
                            {request.executive_name ||
                              'Unknown'}
                          </strong>

                          <div
                            style={{
                              fontSize: 12,
                              opacity: 0.65
                            }}
                          >
                            ID:{' '}
                            {request.sales_executive_id}
                          </div>

                        </div>

                      </div>

                    </td>


                    {/* CODE */}

                    <td>
                      {request.executive_code ||
                        '—'}
                    </td>


                    {/* AMOUNT */}

                    <td>
                      <strong>
                        {formatAmount(
                          request.amount
                        )}
                      </strong>
                    </td>


                    {/* STATUS */}

                    <td>

                      <span
                        className={`status-badge status-${request.status}`}
                      >
                        {STATUS_LABELS[
                          request.status
                        ] || request.status}
                      </span>

                    </td>


                    {/* REQUESTED */}

                    <td>
                      {formatDate(
                        request.created_at
                      )}
                    </td>


                    {/* REVIEWED */}

                    <td>
                      {formatDate(
                        request.reviewed_at
                      )}
                    </td>


                    {/* PAID */}

                    <td>
                      {formatDate(
                        request.paid_at
                      )}
                    </td>


                    {/* ACTIONS */}

                    <td>

                      {request.status ===
                        'pending' && (
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap'
                          }}
                        >

                          <button
                            type="button"
                            className="dash-link-btn activate"
                            disabled={
                              actionLoading ===
                              request.id
                            }
                            onClick={() =>
  setActionDialog({
    open: true,
    type: 'approve',
    request,
    remarks: ''
  })
}
                          >
                            {actionLoading ===
                            request.id
                              ? 'Processing...'
                              : 'Approve'}
                          </button>


                          <button
                            type="button"
                            className="dash-link-btn deactivate"
                            disabled={
                              actionLoading ===
                              request.id
                            }
                            onClick={() =>
  setActionDialog({
    open: true,
    type: 'reject',
    request,
    remarks: ''
  })
}
                          >
                            Reject
                          </button>

                        </div>
                      )}


                      {request.status ===
                        'approved' && (
                        <button
                          type="button"
                          className="dash-link-btn activate"
                          disabled={
                            actionLoading ===
                            request.id
                          }
                          onClick={() =>
  setActionDialog({
    open: true,
    type: 'paid',
    request,
    remarks: ''
  })
}
                        >
                          {actionLoading ===
                          request.id
                            ? 'Processing...'
                            : 'Mark Paid'}
                        </button>
                      )}


                      {(request.status ===
                        'rejected' ||
                        request.status ===
                          'paid') && (
                        <button
                          type="button"
                          className="dash-link-btn"
                          onClick={() =>
                            setSelectedRequest(
                              request
                            )
                          }
                        >
                          View
                        </button>
                      )}

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        )}


        {/* REQUEST PAGINATION */}

        {!loading &&
          pagination.pages > 1 && (

          <div className="glass-pagination">

            <button
              type="button"
              disabled={
                pagination.page <= 1
              }
              onClick={() =>
                loadRequests(
                  pagination.page - 1
                )
              }
            >
              Previous
            </button>


            <span>
              Page {pagination.page} of{' '}
              {pagination.pages}
            </span>


            <button
              type="button"
              disabled={
                pagination.page >=
                pagination.pages
              }
              onClick={() =>
                loadRequests(
                  pagination.page + 1
                )
              }
            >
              Next
            </button>

          </div>

        )}

      </div>


      {/* EARNINGS HISTORY */}

      <div
        className="glass-panel glass-table-panel"
        style={{
          marginTop: 20
        }}
      >

        <div
          style={{
            marginBottom: 18
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18
            }}
          >
            Commission Earnings History
          </h2>

          <p
            style={{
              margin: '6px 0 0',
              opacity: 0.65,
              fontSize: 13
            }}
          >
            Commission amounts earned from vendor activity.
          </p>
        </div>


        {earningsLoading ? (

          <div className="glass-empty-state">
            Loading commission earnings...
          </div>

        ) : earnings.length === 0 ? (

          <div className="glass-empty-state">
            No commission earnings found.
          </div>

        ) : (

          <div className="dash-directory-table-wrap">

            <table className="dash-directory-table">

              <thead>

                <tr>

                  <th>
                    Sales Executive
                  </th>

                  <th>
                    Vendor
                  </th>

                  <th>
                    Commission Type
                  </th>

                  <th>
                    Description
                  </th>

                  <th>
                    Amount
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Earned On
                  </th>

                </tr>

              </thead>


              <tbody>

                {earnings.map((earning) => (

                  <tr key={earning.id}>

                    {/* EXECUTIVE */}

                    <td>

                      <div className="glass-vendor-cell">

                        <div className="glass-avatar-tile">
                          {getInitials(
                            earning.executive_name
                          )}
                        </div>

                        <div>

                          <strong>
                            {earning.executive_name ||
                              'Unknown'}
                          </strong>

                          <div
                            style={{
                              fontSize: 12,
                              opacity: 0.65
                            }}
                          >
                            {earning.executive_code ||
                              '—'}
                          </div>

                        </div>

                      </div>

                    </td>


                    {/* VENDOR */}

                    <td>
                      {earning.vendor_name ||
                        '—'}
                    </td>


                    {/* TYPE */}

                    <td>
                      {earning.commission_type ||
                        '—'}
                    </td>


                    {/* DESCRIPTION */}

                    <td>
                      {earning.description ||
                        '—'}
                    </td>


                    {/* AMOUNT */}

                    <td>
                      <strong>
                        {formatAmount(
                          earning.amount
                        )}
                      </strong>
                    </td>


                    {/* STATUS */}

                    <td>

                      <span
                        className={`status-badge status-${earning.status}`}
                      >
                        {earning.status || '—'}
                      </span>

                    </td>


                    {/* EARNED */}

                    <td>
                      {formatDate(
                        earning.earned_at
                      )}
                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        )}


        {/* EARNINGS PAGINATION */}

        {!earningsLoading &&
          earningsPagination.pages > 1 && (

          <div className="glass-pagination">

            <button
              type="button"
              disabled={
                earningsPagination.page <= 1
              }
              onClick={() =>
                loadEarnings(
                  earningsPagination.page - 1
                )
              }
            >
              Previous
            </button>


            <span>
              Page {earningsPagination.page} of{' '}
              {earningsPagination.pages}
            </span>


            <button
              type="button"
              disabled={
                earningsPagination.page >=
                earningsPagination.pages
              }
              onClick={() =>
                loadEarnings(
                  earningsPagination.page + 1
                )
              }
            >
              Next
            </button>

          </div>

        )}

      </div>


      {/* REQUEST DETAILS MODAL */}

      {selectedRequest && (

        <div
          className="dash-modal-backdrop"
          onClick={() =>
            setSelectedRequest(null)
          }
        >

          <div
            className="dash-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="dash-modal-header">

              <div>

                <h2>
                  Commission Request
                </h2>

                <p>
                  Request #{selectedRequest.id}
                </p>

              </div>


              <button
                type="button"
                className="dash-modal-close"
                onClick={() =>
                  setSelectedRequest(null)
                }
              >
                ×
              </button>

            </div>


            <div className="dash-modal-content">

              {/* EXECUTIVE */}

              <div
                className="glass-vendor-cell"
                style={{
                  marginBottom: 24
                }}
              >

                <div className="glass-avatar-tile">
                  {getInitials(
                    selectedRequest.executive_name
                  )}
                </div>

                <div>

                  <strong>
                    {selectedRequest.executive_name ||
                      'Unknown'}
                  </strong>

                  <div
                    style={{
                      fontSize: 12,
                      opacity: 0.65
                    }}
                  >
                    {selectedRequest.executive_code ||
                      'No executive code'}
                  </div>

                </div>

              </div>


              {/* DETAILS GRID */}

              <div
                className="glass-detail-grid"
              >

                <div>
                  <span>
                    Request ID
                  </span>

                  <strong>
                    #{selectedRequest.id}
                  </strong>
                </div>


                <div>
                  <span>
                    Executive ID
                  </span>

                  <strong>
                    {selectedRequest.sales_executive_id}
                  </strong>
                </div>


                <div>
                  <span>
                    Amount
                  </span>

                  <strong>
                    {formatAmount(
                      selectedRequest.amount
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    Status
                  </span>

                  <strong>
                    {STATUS_LABELS[
                      selectedRequest.status
                    ] ||
                      selectedRequest.status}
                  </strong>
                </div>


                <div>
                  <span>
                    Requested On
                  </span>

                  <strong>
                    {formatDate(
                      selectedRequest.created_at
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    Reviewed On
                  </span>

                  <strong>
                    {formatDate(
                      selectedRequest.reviewed_at
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    Paid On
                  </span>

                  <strong>
                    {formatDate(
                      selectedRequest.paid_at
                    )}
                  </strong>
                </div>


                <div>
                  <span>
                    Reviewed By
                  </span>

                  <strong>
                    {selectedRequest.reviewed_by ||
                      '—'}
                  </strong>
                </div>

              </div>


              {/* ADMIN REMARKS */}

              <div
                style={{
                  marginTop: 24
                }}
              >

                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    opacity: 0.65,
                    marginBottom: 8,
                    textTransform:
                      'uppercase',
                    letterSpacing: '0.04em'
                  }}
                >
                  Admin Remarks
                </div>

                <div
                  style={{
                    padding: 14,
                    borderRadius: 14,
                    background:
                      'rgba(255,255,255,0.35)',
                    border:
                      '1px solid rgba(255,255,255,0.45)',
                    minHeight: 50
                  }}
                >
                  {selectedRequest.admin_remarks ||
                    'No remarks'}
                </div>

              </div>

            </div>


            {/* MODAL ACTIONS */}

            {selectedRequest.status ===
              'pending' && (

              <div
                className="dash-modal-actions"
              >

                <button
                  type="button"
                  className="dash-link-btn deactivate"
                  disabled={
                    actionLoading ===
                    selectedRequest.id
                  }
                  onClick={() =>
  setActionDialog({
    open: true,
    type: 'reject',
    request: selectedRequest,
    remarks: ''
  })
}
                >
                  Reject
                </button>


                <button
                  type="button"
                  className="dash-link-btn activate"
                  disabled={
                    actionLoading ===
                    selectedRequest.id
                  }
                  onClick={() =>
  setActionDialog({
    open: true,
    type: 'approve',
    request: selectedRequest,
    remarks: ''
  })
}
                >
                  {actionLoading ===
                  selectedRequest.id
                    ? 'Processing...'
                    : 'Approve'}
                </button>

              </div>

            )}


            {selectedRequest.status ===
              'approved' && (

              <div
                className="dash-modal-actions"
              >

                <button
                  type="button"
                  className="dash-link-btn activate"
                  disabled={
                    actionLoading ===
                    selectedRequest.id
                  }
                 onClick={() =>
  setActionDialog({
    open: true,
    type: 'paid',
    request: selectedRequest,
    remarks: ''
  })
}
                >
                  {actionLoading ===
                  selectedRequest.id
                    ? 'Processing...'
                    : 'Mark Paid'}
                </button>

              </div>

            )}

                 

          </div>

        </div>

      )}
       {/* ACTION CONFIRMATION DIALOG */}

      {actionDialog.open && actionDialog.request && (
        <div
          className="dash-modal-backdrop"
          onClick={() =>
            setActionDialog({
              open: false,
              type: null,
              request: null,
              remarks: ''
            })
          }
        >
          <div
            className="dash-modal"
            onClick={(e) => e.stopPropagation()}
          >

            <div className="dash-modal-header">
              <div>
                <h2>
                  {actionDialog.type === 'approve'
                    ? 'Approve Withdrawal'
                    : actionDialog.type === 'reject'
                    ? 'Reject Withdrawal'
                    : 'Mark Payment Paid'}
                </h2>

                <p>
                  Request #{actionDialog.request.id}
                </p>
              </div>

              <button
                type="button"
                className="dash-modal-close"
                onClick={() =>
                  setActionDialog({
                    open: false,
                    type: null,
                    request: null,
                    remarks: ''
                  })
                }
              >
                ×
              </button>
            </div>

            <div className="dash-modal-content">

              <p style={{ marginTop: 0 }}>
                {actionDialog.type === 'approve' &&
                  `Approve commission withdrawal of ${formatAmount(
                    actionDialog.request.amount
                  )}?`}

                {actionDialog.type === 'paid' &&
                  `Mark commission withdrawal of ${formatAmount(
                    actionDialog.request.amount
                  )} as paid?`}

                {actionDialog.type === 'reject' &&
                  `Reject commission withdrawal of ${formatAmount(
                    actionDialog.request.amount
                  )}?`}
              </p>

              {actionDialog.type === 'reject' && (
                <div style={{ marginTop: 18 }}>
                  <label
                    htmlFor="commissionRejectRemarks"
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 700,
                      opacity: 0.65,
                      marginBottom: 8,
                      textTransform: 'uppercase'
                    }}
                  >
                    Rejection Reason
                  </label>

                  <textarea
                    id="commissionRejectRemarks"
                    className="admin-text-input"
                    rows={4}
                    maxLength={500}
                    value={actionDialog.remarks}
                    onChange={(e) =>
                      setActionDialog((prev) => ({
                        ...prev,
                        remarks: e.target.value
                      }))
                    }
                    placeholder="Enter rejection reason..."
                  />
                </div>
              )}

            </div>

            <div className="dash-modal-actions">

              <button
                type="button"
                className="dash-link-btn"
                disabled={
                  actionLoading ===
                  actionDialog.request.id
                }
                onClick={() =>
                  setActionDialog({
                    open: false,
                    type: null,
                    request: null,
                    remarks: ''
                  })
                }
              >
                Cancel
              </button>

              {actionDialog.type === 'approve' && (
                <button
                  type="button"
                  className="dash-link-btn activate"
                  disabled={
                    actionLoading ===
                    actionDialog.request.id
                  }
                  onClick={() =>
                    handleApprove(
                      actionDialog.request
                    )
                  }
                >
                  {actionLoading ===
                  actionDialog.request.id
                    ? 'Processing...'
                    : 'Approve'}
                </button>
              )}

              {actionDialog.type === 'reject' && (
                <button
                  type="button"
                  className="dash-link-btn deactivate"
                  disabled={
                    actionLoading ===
                    actionDialog.request.id
                  }
                  onClick={() =>
                    handleReject(
                      actionDialog.request
                    )
                  }
                >
                  {actionLoading ===
                  actionDialog.request.id
                    ? 'Processing...'
                    : 'Reject'}
                </button>
              )}

              {actionDialog.type === 'paid' && (
                <button
                  type="button"
                  className="dash-link-btn activate"
                  disabled={
                    actionLoading ===
                    actionDialog.request.id
                  }
                  onClick={() =>
                    handleMarkPaid(
                      actionDialog.request
                    )
                  }
                >
                  {actionLoading ===
                  actionDialog.request.id
                    ? 'Processing...'
                    : 'Mark Paid'}
                </button>
              )}

            </div>

          </div>
        </div>
      )}

    </div>
    </AdminShell>
  );
};

export default CommissionManagement;